import { Modal, Text, Flex, Button, ThemeIcon, Paper, Box } from "@mantine/core";
import { IconConfetti, IconCoins, IconShield, IconLock } from "@tabler/icons-react";

const BRAND_NAVY = "var(--mantine-color-primary-6)";
const BRAND_YELLOW = "#ffc300";
const CREDITS_GREEN = "var(--mantine-color-green-6)";

const MODAL_AMBIENT_BG = `
  radial-gradient(ellipse 95% 75% at 0% 0%, rgba(59, 130, 246, 0.2) 0%, transparent 52%),
  radial-gradient(ellipse 90% 70% at 100% 100%, rgba(37, 99, 235, 0.14) 0%, transparent 50%),
  linear-gradient(180deg, rgba(255, 255, 255, 0.03) 0%, transparent 35%)
`;

interface ProfileWelcomeCreditsModalProps {
  opened: boolean;
  onClose: () => void;
  onGetVerified: () => void;
  signupBonusCredits: number;
}

export default function ProfileWelcomeCreditsModal({
  opened,
  onClose,
  onGetVerified,
  signupBonusCredits,
}: ProfileWelcomeCreditsModalProps) {
  const creditsLabel = `${signupBonusCredits} Free Credits!`;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      size="lg"
      radius="lg"
      padding={0}
      withCloseButton
      overlayProps={{ backgroundOpacity: 0.6, blur: 4 }}
      styles={{
        content: {
          overflow: "hidden",
          backgroundColor: BRAND_NAVY,
          backgroundImage: MODAL_AMBIENT_BG,
          border: "none",
        },
        header: {
          backgroundColor: "transparent",
          paddingTop: 12,
          paddingRight: 12,
          minHeight: 0,
        },
        close: {
          color: "white",
          "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.12)" },
        },
        body: {
          position: "relative",
          padding: "8px 28px 28px",
        },
      }}
    >
      <Box pos="relative">
        <Flex direction="column" align="center" gap="lg">
          <ThemeIcon
            size={80}
            radius="xl"
            style={{
              backgroundColor: BRAND_YELLOW,
              border: "none",
            }}
          >
            <IconConfetti size={44} color={BRAND_NAVY} stroke={1.5} />
          </ThemeIcon>

          <Box ta="center">
            <Text fw={700} fz={{ base: 24, sm: 28 }} c="white" lh={1.25}>
              Welcome! You&apos;re All Set!
            </Text>
            <Text fz="sm" c="rgba(255, 255, 255, 0.88)" mt="sm" maw={420} mx="auto" lh={1.55}>
              We&apos;re thrilled to have you on board. Let&apos;s start something{" "}
              <Text span fw={700} c={BRAND_YELLOW}>
                amazing
              </Text>{" "}
              together!
            </Text>
          </Box>

          <Paper
            w="100%"
            p="lg"
            radius="lg"
            bg="white"
            style={{ border: "none", boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)" }}
          >
            <Flex align="flex-start" gap="md">
              <ThemeIcon
                size={52}
                radius="xl"
                style={{
                  backgroundColor: CREDITS_GREEN,
                  flexShrink: 0,
                }}
              >
                <IconCoins size={28} color="white" stroke={1.5} />
              </ThemeIcon>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Text fz="xs" c={BRAND_NAVY} mb={4} opacity={0.85}>
                  You have earned
                </Text>
                <Text fw={800} fz={{ base: 22, sm: 26 }} c="green.7" lh={1.2}>
                  {creditsLabel}
                </Text>
                <Text fz="sm" c={BRAND_NAVY} mt="sm" lh={1.55}>
                  Use your{" "}
                  <Text span fw={700} inherit>
                    {signupBonusCredits} free credits
                  </Text>{" "}
                  for your{" "}
                  <Text span fw={700} inherit>
                    1st collaboration
                  </Text>{" "}
                  and take your first step towards success!
                </Text>
              </Box>
            </Flex>
          </Paper>

          <Box w="100%">
            <Button
              fullWidth
              size="md"
              leftSection={<IconShield size={18} color={BRAND_NAVY} />}
              onClick={onGetVerified}
              styles={{
                root: {
                  backgroundColor: BRAND_YELLOW,
                  color: BRAND_NAVY,
                  fontWeight: 700,
                  border: "none",
                  "&:hover": {
                    backgroundColor: "#e6b000",
                  },
                },
                label: {
                  color: BRAND_NAVY,
                },
              }}
            >
              Get verified to start collaborating
            </Button>

            <Flex justify="center" align="center" gap={6} mt="md">
              <IconLock size={14} color="rgba(255, 255, 255, 0.75)" stroke={1.5} />
              <Text fz="xs" c="rgba(255, 255, 255, 0.75)" ta="center">
                Verification is quick, easy and secure.
              </Text>
            </Flex>
          </Box>
        </Flex>
      </Box>
    </Modal>
  );
}
